import React, {useState} from 'react';
import DisposalDialog from './DisposalDialog';
import {DisposalIntent} from '../../support/itemLifecycle';
import {DISPOSAL_METHOD_OPTIONS} from '../../support/disposalMethods';
import {pluralItems} from '../../utils/plural';

/**
 * Выбор решения по кандидатам.
 *
 * <p>Раньше здесь была кнопка «В корзину»: она не называла ни принятого решения, ни того, куда
 * вещь попадёт дальше. Корзины в модели больше нет — есть решение расстаться, и у решения есть
 * способ. Способ влияет на аналитику и экологическую статистику, поэтому у каждого варианта
 * стоит определение, а не одно слово.</p>
 *
 * <p>Решение обратимо: вещь остаётся у владельца и только ждёт передачи. Об этом сказано в
 * диалоге, иначе выбор способа читается как необратимое действие.</p>
 */
export default function DecideIntentDialog({count, busy, onConfirm, onCancel}: {
    count: number;
    busy?: boolean;
    onConfirm: (intent: DisposalIntent) => void;
    onCancel: () => void;
}) {
    const [intent, setIntent] = useState<DisposalIntent>(DISPOSAL_METHOD_OPTIONS[0].value);

    return (
        <DisposalDialog
            title={`Решить расстаться: ${pluralItems(count)}`}
            confirmLabel={busy ? 'Сохраняем…' : 'Принять решение'}
            onConfirm={() => onConfirm(intent)}
            onCancel={onCancel}
        >
            <p>Что вы планируете сделать с этими вещами?</p>
            <div role="radiogroup" aria-label="Способ расставания" className="space-y-1"
                 data-testid="decide-intent-options">
                {DISPOSAL_METHOD_OPTIONS.map(o => (
                    <label key={o.value}
                           className={`flex items-start gap-3 min-h-[44px] px-3 py-2 rounded-xl cursor-pointer
                                       border transition-colors duration-ui ease-out ${
                               intent === o.value
                                   ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                   : 'border-gray-200 dark:border-ink-700 hover:border-brand-500'
                           }`}>
                        <input
                            type="radio"
                            name="decide-intent"
                            value={o.value}
                            checked={intent === o.value}
                            onChange={() => setIntent(o.value)}
                            data-testid={`decide-intent-${o.value}`}
                            className="mt-1 w-5 h-5 shrink-0 accent-brand-600"
                        />
                        <span className="min-w-0">
                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                                {o.intentLabel}
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">{o.hint}</span>
                        </span>
                    </label>
                ))}
            </div>
            <p className="text-gray-500 dark:text-gray-400">
                Вещи перейдут на стадию «Ждут передачи»: они остаются у вас, из обычных подборов
                исключаются, а решение можно отменить в любой момент.
            </p>
        </DisposalDialog>
    );
}
